import React from 'react';
import { Alert } from '@planne-software/uni/Alert';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCircleNumber1Filled,
  IconCircleNumber2Filled,
  IconShieldCheckFilled,
} from '@tabler/icons-react';

import { useTranslate } from '@src/core/hooks';
import type { PaymentOption } from '@src/domains/payments';
import type { Transactable } from '@src/domains/transactables';
import { usePaymentErrors } from '@src/features/payments/errors';
import { useProcessPayment } from '@src/features/payments/hooks/useProcessPayment';

import { ActionRow, GenerateButton, MethodBodyRoot, SecurePurchase, StepList, StepRow } from './styles';

type PixMethodBodyProps = {
  paymentOption: PaymentOption;
  transactable: Transactable;
  onPaid?: () => void;
};

export const PixMethodBody = (props: PixMethodBodyProps) => {
  const { paymentOption, transactable, onPaid } = props;
  const { i18n, i18nElement } = useTranslate();

  const { pay, submitting } = useProcessPayment(transactable);
  const { hasInsufficientAmount } = usePaymentErrors();

  const generate = async () => {
    const result = await pay(paymentOption.paymentMethod.id);

    if (result?.success) {
      onPaid?.();
    }
  };

  return (
    <MethodBodyRoot data-testid='pix-method-body'>
      {hasInsufficientAmount ? (
        <Alert
          data-testid='pix-insufficient-amount'
          severity='warning'
          startIcon={<IconAlertTriangle size={20} aria-hidden />}
          title={i18n('checkout_pix_payment_insufficient_amount_title')}
          subTitle={i18n('checkout_pix_payment_insufficient_amount_description')}
        />
      ) : null}

      <StepList>
        <StepRow>
          <IconCircleNumber1Filled size={24} aria-hidden />
          <span>{i18nElement('checkout_payment_pix_step_generate')}</span>
        </StepRow>
        <StepRow>
          <IconCircleNumber2Filled size={24} aria-hidden />
          <span>{i18n('checkout_payment_pix_step_confirm')}</span>
        </StepRow>
      </StepList>

      <ActionRow>
        <SecurePurchase>
          <IconShieldCheckFilled size={20} aria-hidden />
          {i18n('checkout_payment_pix_secure_purchase')}
        </SecurePurchase>

        <GenerateButton
          variant='contained'
          size='lg'
          loading={submitting}
          disabled={submitting || hasInsufficientAmount}
          onClick={generate}
          endSlot={<IconArrowRight size={20} aria-hidden />}
        >
          {i18n('checkout_payment_pix_generate_button')}
        </GenerateButton>
      </ActionRow>
    </MethodBodyRoot>
  );
};

export default PixMethodBody;
